import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StampSerial, StatusBadge, RM, FlagPill } from "@/components/shared";
import { Search, Plus, FileSpreadsheet, FileText, Loader2, X, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const YEARS = ["", "2011", "2012", "2013", "2014", "2015", "2016", "2017", "2018", "2019", "2020", "2021", "2022", "2023", "2024", "2025"];
const MISSING = [
  { v: "", l: "Semua / All" },
  { v: "missing_email", l: "Missing email" },
  { v: "missing_mobile", l: "Missing mobile" },
  { v: "missing_ic", l: "Missing IC" },
  { v: "missing_dob", l: "Missing DOB" },
  { v: "unconfirmed_balance", l: "Unconfirmed balance" },
];

export default function Members() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [year, setYear] = useState("");
  const [missing, setMissing] = useState(params.get("missing") || "");
  const [page, setPage] = useState(1);
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const p = { page, page_size: 25 };
    if (search) p.search = search;
    if (status) p.status = status;
    if (year) p.year = year;
    if (missing) p.missing = missing;
    api.get("/members", { params: p }).then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [page, search, status, year, missing]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [search, status, year, missing]);

  const exportFile = async (fmt) => {
    try {
      const res = await api.get(`/members/export/${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `kkpklb_register.${fmt === "excel" ? "xlsx" : "pdf"}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Export failed");
    }
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;

  return (
    <Layout
      title="Daftar Ahli / Members Register"
      subtitle={data ? `${data.total} ahli / members` : "—"}
      actions={
        <>
          <button onClick={() => exportFile("excel")} data-testid="export-excel-btn" className="hidden sm:flex items-center gap-1.5 bg-transparent border border-ink/20 text-ink hover:bg-ink/5 px-3 py-2 rounded-sm text-sm transition-colors">
            <FileSpreadsheet size={15} /> Excel
          </button>
          <button onClick={() => exportFile("pdf")} data-testid="export-pdf-btn" className="hidden sm:flex items-center gap-1.5 bg-transparent border border-ink/20 text-ink hover:bg-ink/5 px-3 py-2 rounded-sm text-sm transition-colors">
            <FileText size={15} /> PDF
          </button>
          <button onClick={() => setShowAdd(true)} data-testid="add-member-btn" className="flex items-center gap-1.5 bg-brass text-ink font-semibold hover:bg-brass-hover px-3 py-2 rounded-sm text-sm transition-colors">
            <Plus size={16} /> <span className="hidden sm:inline">Tambah / Add</span>
          </button>
        </>
      }
    >
      {/* Filters */}
      <div className="bg-white border border-hairline rounded-sm p-4 mb-6 flex flex-col md:flex-row gap-3 md:items-center">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate/50" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="member-search"
            placeholder="Cari nama, no. ahli, IC / Search name, membership no, IC"
            className="w-full bg-paper border border-slate-300 rounded-sm pl-9 pr-3 py-2 text-sm focus:border-brass focus:ring-1 focus:ring-brass outline-none text-ink"
          />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="filter-status" className="bg-paper border border-slate-300 rounded-sm px-3 py-2 text-sm text-ink outline-none focus:border-brass">
          <option value="">Semua status / All</option>
          <option value="active">Active</option>
          <option value="resigned">Resigned</option>
          <option value="deceased">Deceased</option>
          <option value="suspended">Suspended</option>
        </select>
        <select value={year} onChange={(e) => setYear(e.target.value)} data-testid="filter-year" className="bg-paper border border-slate-300 rounded-sm px-3 py-2 text-sm text-ink outline-none focus:border-brass">
          {YEARS.map((y) => <option key={y} value={y}>{y || "Semua tahun / All years"}</option>)}
        </select>
        <select value={missing} onChange={(e) => setMissing(e.target.value)} data-testid="filter-missing" className="bg-paper border border-slate-300 rounded-sm px-3 py-2 text-sm text-ink outline-none focus:border-brass">
          {MISSING.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
        </select>
      </div>

      {/* Ledger table */}
      <div className="bg-white border border-hairline rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-paper border-y border-ink/20">
                <th className="text-left text-xs uppercase tracking-wider text-slate font-medium py-3 px-4">No</th>
                <th className="text-left text-xs uppercase tracking-wider text-slate font-medium py-3 px-4">No. Ahli / Membership</th>
                <th className="text-left text-xs uppercase tracking-wider text-slate font-medium py-3 px-4">Nama / Name</th>
                <th className="text-left text-xs uppercase tracking-wider text-slate font-medium py-3 px-4">I.C. No</th>
                <th className="text-right text-xs uppercase tracking-wider text-slate font-medium py-3 px-4">Syer / Share (RM)</th>
                <th className="text-left text-xs uppercase tracking-wider text-slate font-medium py-3 px-4">Status</th>
                <th className="text-left text-xs uppercase tracking-wider text-slate font-medium py-3 px-4">Flags</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="py-16 text-center text-slate"><Loader2 className="animate-spin inline mr-2" size={18} />Loading…</td></tr>
              ) : data.members.length === 0 ? (
                <tr><td colSpan={7} className="py-16 text-center text-slate">Tiada rekod / No records found.</td></tr>
              ) : (
                data.members.map((m) => {
                  const flags = (m.flags || []).concat(
                    [!m.email && "missing_email", !m.mobile && "missing_mobile", !m.ic_last4 && "missing_ic", !m.opening_balance_confirmed && "unconfirmed_balance"].filter(Boolean)
                  );
                  const uniqFlags = [...new Set(flags)];
                  return (
                    <tr
                      key={m.id}
                      onClick={() => nav(`/members/${m.id}`)}
                      data-testid={`member-row-${m.membership_no}`}
                      className="ledger-row hover:bg-paper/70 cursor-pointer transition-colors"
                    >
                      <td className="py-2.5 px-4 font-mono text-sm text-slate">{m.no ?? "—"}</td>
                      <td className="py-2.5 px-4"><StampSerial value={m.membership_no} /></td>
                      <td className="py-2.5 px-4 text-sm text-ink">
                        <span className="text-slate text-xs">{m.title !== "(none)" ? m.title + " " : ""}</span>{m.name}
                      </td>
                      <td className="py-2.5 px-4 font-mono text-sm text-slate">{m.ic_display}</td>
                      <td className="py-2.5 px-4 text-right text-sm">
                        {m.opening_balance_confirmed ? <RM value={m.share_balance} /> : <span className="text-brass text-xs italic">unconfirmed</span>}
                      </td>
                      <td className="py-2.5 px-4"><StatusBadge status={m.status} /></td>
                      <td className="py-2.5 px-4 max-w-[200px]">{uniqFlags.slice(0, 3).map((f) => <FlagPill key={f} flag={f} />)}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {data && data.total > data.page_size && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-hairline">
            <span className="text-xs text-slate">Halaman {page} / {totalPages}</span>
            <div className="flex gap-2">
              <button disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="prev-page" className="p-1.5 border border-hairline rounded-sm disabled:opacity-40 hover:bg-paper"><ChevronLeft size={16} /></button>
              <button disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="next-page" className="p-1.5 border border-hairline rounded-sm disabled:opacity-40 hover:bg-paper"><ChevronRight size={16} /></button>
            </div>
          </div>
        )}
      </div>

      {showAdd && <AddMemberModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
    </Layout>
  );
}

function AddMemberModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", title: "", ic: "", dob: "", email: "", mobile: "", address: "", occupation: "", join_date: "", opening_balance: "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.opening_balance === "") delete payload.opening_balance;
      else payload.opening_balance = parseFloat(payload.opening_balance);
      const r = await api.post("/members", payload);
      toast.success(`Member ${r.data.membership_no} created`);
      onSaved();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const field = (k, label, type = "text", placeholder = "") => (
    <div>
      <label className="block text-xs font-medium text-slate mb-1">{label}</label>
      <input type={type} value={form[k]} onChange={set(k)} placeholder={placeholder} data-testid={`add-${k}`}
        className="w-full bg-white border border-slate-300 rounded-sm px-3 py-2 text-sm focus:border-brass focus:ring-1 focus:ring-brass outline-none text-ink" />
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(18,42,67,0.4)" }} onClick={onClose}>
      <div className="bg-white rounded-md shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-fade-in" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-hairline sticky top-0 bg-white">
          <h3 className="font-serif text-xl text-ink">Tambah Ahli / Add Member</h3>
          <button onClick={onClose} className="text-slate hover:text-ink" data-testid="add-close"><X size={20} /></button>
        </div>
        <div className="p-6 grid md:grid-cols-2 gap-4">
          {field("name", "Nama / Name *")}
          {field("title", "Gelaran / Title", "text", "Dr., En., Ms")}
          {field("ic", "I.C. No", "text", "850726-14-5112")}
          {field("dob", "Tarikh Lahir / DOB", "date")}
          {field("email", "Emel / Email", "email")}
          {field("mobile", "Mobil / Mobile")}
          {field("join_date", "Tarikh Sertai / Join Date", "date")}
          {field("opening_balance", "Baki Awal / Opening Balance (RM)", "number", "0.00")}
          <div className="md:col-span-2">{field("address", "Alamat / Address")}</div>
          {field("occupation", "Pekerjaan / Occupation")}
        </div>
        <div className="px-6 py-4 border-t border-hairline flex justify-end gap-3 sticky bottom-0 bg-white">
          <button onClick={onClose} className="bg-transparent border border-ink/20 text-ink hover:bg-ink/5 px-4 py-2 rounded-sm text-sm transition-colors">Batal / Cancel</button>
          <button onClick={save} disabled={saving} data-testid="add-save" className="bg-ink text-white hover:bg-ink-light px-4 py-2 rounded-sm text-sm flex items-center gap-2 transition-colors disabled:opacity-60">
            {saving && <Loader2 size={15} className="animate-spin" />} Simpan / Save
          </button>
        </div>
      </div>
    </div>
  );
}
